/**
 * runStepsInputs — the ONE place that decides what the Steps log is shown.
 *
 * `DetailedRunLog` and `buildRunLog` were shared between ora and the Outlook
 * plugin from the start; their INPUTS were not, and that is where the two
 * surfaces drifted. `buildRunLog` can only report that a step never ran by
 * comparing the run's chronology against the workflow's DECLARED manifest, so
 * an empty manifest silently downgrades the log to "every row the server
 * reported, all of them green" — a different and much more flattering claim
 * than the truth.
 *
 * That is exactly what the plugin rendered for
 * `81363a33-7ea0-46ff-9a1e-fd1e7ddc153c`: 45 chronology rows all painted
 * complete, with the 17 declared steps that never ran missing from the list
 * entirely, while ora — which resolved the catalog entry itself — marked them
 * skipped / not reached. The plugin took its catalog entry from a HOST prop,
 * and two of its open paths (the ora-notification landing and
 * `Action.InvokeAddInCommand`) legitimately have no workflow id to resolve one
 * with, because the run, not the caller, is what identifies the workflow.
 *
 * So the derivation moves here and keys off the RUN's own status payload. A
 * host may still pass an entry it already holds, but it can no longer be the
 * only source, and neither host may re-implement any of this: a Steps view
 * that differs between the two apps is a bug in one of them, and there is now
 * only one implementation to be wrong.
 *
 * Everything here is PURE — no React, no transport, no clock. Import paths:
 *   ora    → ~/common/workflows/drawer/sections/runStepsInputs
 *   plugin → @product-app/common/workflows/drawer/sections/runStepsInputs
 */

import type { InstanceStatusSnapshot, PhaseDefinition } from "../../types";
import type { RunLogStep } from "./runLogModel";

/** The members of a catalog entry this module reads. Both hosts' catalog entry
 *  types satisfy it structurally, so neither has to be imported here. */
export interface StepCatalogEntryLike {
  id?: string | null;
  /** The substrate publishes `ref` and `id` as the same workflow id today, but
   *  they are separate wire fields and ora has always matched on either. */
  ref?: string | null;
  steps?: readonly StepCatalogStepLike[] | null;
}

export interface StepCatalogStepLike {
  id: string;
  name?: string | null;
  /** RAW catalog kind ("rpa" / "hitl" / "python" / …). Never the normalized
   *  domain-badge category — `formatKind` does not recognise that one. */
  type?: string | null;
  hitl?: boolean | null;
}

/**
 * Resolve the catalog entry that DECLARES this run's steps.
 *
 * `workflowIds` is tried in order, so a caller passes the run's own
 * `status.workflow_id` first and any host-side hint (a grid row's workflow
 * type, the drawer state's launch ref) after it. Blank and nullish ids are
 * skipped rather than matched: `catalogMap.get("")` returning nothing is how
 * the plugin lost its manifest in the first place, and a lookup that cannot
 * succeed should not look like one that failed to find anything.
 */
export function resolveStepCatalogEntry<T extends StepCatalogEntryLike>(
  catalog: readonly T[] | null | undefined,
  ...workflowIds: (string | null | undefined)[]
): T | null {
  if (!catalog || catalog.length === 0) return null;
  for (const id of workflowIds) {
    if (!id) continue;
    const hit = catalog.find((entry) => entry.id === id || entry.ref === id);
    if (hit) return hit;
  }
  return null;
}

/**
 * The workflow's declared steps, in declared order — the spine the Steps log
 * is ordered by.
 *
 * `label` falls back to the step id when the catalog reports no display name.
 * The id is a poor label but it is the server's own string; the alternative is
 * a blank row, and the log's honesty rule is that it never invents a name.
 * `kind` stays the RAW catalog kind for the tooltip's Kind row.
 */
export function buildRunLogManifest(
  entry: StepCatalogEntryLike | null | undefined,
): RunLogStep[] {
  const steps = entry?.steps;
  if (!steps || steps.length === 0) return [];
  return steps.map((step) => ({
    id: step.id,
    label: step.name || step.id,
    kind: step.type ?? null,
  }));
}

/** Statuses that end a run in a failure-like way. Kept local so this module
 *  imports no host vocabulary; `sectionOrder.getSectionOrder` lists the same
 *  three alongside `completed`. */
const FAILED_STATUSES: ReadonlySet<string> = new Set([
  "failed",
  "rejected",
  "timeout_cancelled",
]);

export interface RunLogPointer {
  currentStepId: string | null;
  isTerminal: boolean;
  isFailed: boolean;
}

/**
 * Resolve the live pointer + terminal/failed flags `buildRunLog` needs.
 *
 * `step_id` FIRST, `current_phase` only as a fallback. Both point at "the step
 * running now" and they do NOT agree: REST resolves `current_phase` to a real
 * step id, while the SignalR delta sends the raw `current_phase` column, which
 * on a phase-graph orchestration is the outer graph node (e.g.
 * `carrier_mortgagee_update_v2.phase_graph`) — and the push fold overwrites the
 * resolved value with it. Keying on `current_phase` therefore made the live
 * marker VANISH after every push, because the graph node matches no step in the
 * manifest. `step_id` on that same delta is `emit_push`'s resolved step id.
 *
 * A terminal run has no live step at all, so the pointer is dropped rather than
 * left pointing at wherever the run stopped.
 */
export function resolveRunLogPointer({
  status,
  isTerminal,
  fallbackStepId = null,
}: {
  status: InstanceStatusSnapshot | null | undefined;
  isTerminal: boolean;
  /** Host-side last-known step (a grid row's current step name). Used only
   *  while the run is live and the status payload reports neither pointer. */
  fallbackStepId?: string | null;
}): RunLogPointer {
  const statusToken = (status?.status ?? "").toLowerCase();
  return {
    currentStepId: isTerminal
      ? null
      : (status?.step_id ?? status?.current_phase ?? fallbackStepId ?? null),
    isTerminal,
    isFailed: FAILED_STATUSES.has(statusToken),
  };
}

/** "epic_policy_search" → "Epic Policy Search". Used only when the catalog
 *  reports no display name; never to replace one it did report. */
function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface BuildPhaseDefinitionsInput {
  /** A manifest the host was handed outright. Wins when non-empty. */
  explicitPhases?: PhaseDefinition[] | null;
  /** The entry that declares this run's steps — resolve it with
   *  `resolveStepCatalogEntry` before calling. */
  catalogEntry?: StepCatalogEntryLike | null;
  /** The run's status payload, for the fallbacks. */
  status?: InstanceStatusSnapshot | null;
  /** The host's last-known step name (a monitor row's `currentStepName`). */
  fallbackStepId?: string | null;
}

/**
 * The phase list the pipeline and the collapsed chip render.
 *
 * `type` is the RAW catalog kind, deliberately. It feeds `formatKind` for the
 * tooltip's Kind row, which knows `python` / `rpa` / `hitl` / `degraded` and
 * passes anything else through verbatim. The plugin used to normalize it into
 * a domain-badge category (`hitl` → `human`) before rendering, which won it
 * badges ora does not show and cost it a legible Kind row ("human" instead of
 * "Human review"). ora is the reference look, so the raw kind wins and the
 * normalization is gone. `domainTypeColors` therefore matches only a workflow
 * whose catalog declares domain categories — which is what the pipeline's own
 * contract has always said.
 *
 * The fallback ORDER is ora's, unchanged: a declared total beats the phases
 * seen so far, because a numbered spine of the right LENGTH keeps the progress
 * bar and the "Step N of M" counter telling the same story. Every fallback is
 * an edge path now that the catalog entry resolves from the run's own
 * `workflow_id` rather than from whatever the host happened to hold.
 */
export function buildPhaseDefinitions({
  explicitPhases,
  catalogEntry,
  status,
  fallbackStepId = null,
}: BuildPhaseDefinitionsInput): PhaseDefinition[] {
  if (explicitPhases && explicitPhases.length > 0) return explicitPhases;

  const steps = catalogEntry?.steps;
  if (steps && steps.length > 0) {
    return steps.map((s) => ({
      id: s.id,
      description: s.name || titleCase(s.id),
      type: s.type || "data",
      hitl_checkpoint: s.hitl || undefined,
    }));
  }

  const total = status?.total_steps ?? null;
  if (total && total > 0) {
    return Array.from({ length: total }, (_, i) => ({
      id: `step_${i}`,
      description: `Step ${i + 1}`,
      type: "data",
    }));
  }

  // Whatever the run has actually reported: the phases in its server-ordered
  // history, plus the one running now. Real step ids, so this beats reporting
  // nothing. Reads `phases` (migration 019) — this used to read
  // `phase_outputs`, which nothing has ever emitted, so the loop ran zero
  // times and this fallback degraded to a single current-step row.
  const seen = new Set<string>();
  const out: PhaseDefinition[] = [];
  for (const row of status?.phases ?? []) {
    const id = row.phase_id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, description: titleCase(id), type: "data" });
  }
  const current = status?.current_phase ?? fallbackStepId ?? null;
  if (current && !seen.has(current)) {
    out.push({ id: current, description: titleCase(current), type: "data" });
  }
  return out;
}
